import { computeBallWon, displayBallResult } from "../shared/squaresBallResults";
import { SQUARES_ENTRANCE_FEE } from "../shared/squaresConstants";
import { FALLBACK_AVATAR_OPTIONS, getAvatarThumbSrc } from "../../../utils/profileAvatarCatalog";
import { computeSuperSquaresBreakdown } from "../../Leagues/lib/leagueSuperSquares";
import { peekActiveLeagueId } from "../../Leagues/lib/leagueModeStore";
import { findLeague, settleOpenPlayWithReturn } from "../../Leagues/lib/leagueStore";
import {
    buildSquaresDemoOddsGrid,
    SQUARES_DEMO_BALL_RESULTS,
    SQUARES_DEMO_OVER_NUMBER,
    SQUARES_DEMO_VIEWER_USER_ID,
    squaresDemoFirstPlacePrize,
    squaresDemoPoolEntrantCount,
    squaresDemoViewerAvatar,
} from "./squaresDemoMatchFixtures";
import {
    PRESENTER_LIVE_BALL_RESULTS,
    PRESENTER_LIVE_OVER,
} from "../../Leagues/lib/leaguePresenterLiveMatch";

function resolveDemoLeagueId(): string | null {
    const fromMode = peekActiveLeagueId();
    if (fromMode) return fromMode;
    if (typeof window === "undefined") return null;
    try {
        return new URLSearchParams(window.location.search).get("leagueId")?.trim() || null;
    } catch {
        return null;
    }
}

function isSuperSquaresDemoContext(): boolean {
    return Boolean(resolveDemoLeagueId());
}

const BALL_COUNT = 6;

export type DemoSelection = Record<
    string,
    { run?: string; runLabel?: string; chips?: number; odds?: number }
>;

export type DemoPlayRow = {
    id: number;
    user_id: number | string | null;
    name: string;
    avatar: string | null;
    profile_pic?: string | null;
    isBot: boolean;
    selections: DemoSelection;
    created_at: string;
    stake: number;
    ball_results: string[] | null;
    rank: number | null;
    score: number | null;
    winnings: number | null;
    settled: boolean;
    outcome: string | null;
    return: number | null;
    totalPot: number | null;
    over_number: number;
    runningChips?: number;
    per_ball?: Array<{ ball: number; result: string; selected: string | null; won: boolean }>;
    super_squares?: ReturnType<typeof computeSuperSquaresBreakdown>;
};

type DemoPhase = "idle" | "simulating" | "settled";

type Listener = () => void;

const DEMO_OPPONENTS = [
    { userId: -9202, name: "Giraffe Pro", avatarIndex: 0, picks: ["2", "4", "0", "1", "2", "5+"] as const },
    { userId: -9203, name: "Croc King", avatarIndex: 1, picks: ["1", "1", "1", "0", "3", "4"] as const },
    { userId: -9204, name: "Rhino Ace", avatarIndex: 2, picks: ["0", "2", "2", "2", "1", "5+"] as const },
    { userId: -9205, name: "Tiger Stripes", avatarIndex: 3, picks: ["4", "4", "4", "4", "4", "4"] as const },
    { userId: -9206, name: "Monkey Business", avatarIndex: 4, picks: ["3", "3", "3", "3", "3", "3"] as const },
    { userId: -9207, name: "Owl Eyes", avatarIndex: 5, picks: ["0", "0", "1", "1", "2", "2"] as const },
    { userId: -9208, name: "Shark Attack", avatarIndex: 6, picks: ["5+", "5+", "4", "4", "3", "3"] as const },
    { userId: -9209, name: "Elephant Power", avatarIndex: 7, picks: ["2", "2", "2", "3", "3", "3"] as const },
    { userId: -9210, name: "Croc Crusher", avatarIndex: 1, picks: ["1", "2", "3", "4", "5+", "0"] as const },
    { userId: -9211, name: "Rhino Rush", avatarIndex: 2, picks: ["5+", "5+", "4", "3", "2", "1"] as const },
];

function demoBotAvatarUrl(avatarIndex: number): string | null {
    const item = FALLBACK_AVATAR_OPTIONS[avatarIndex % FALLBACK_AVATAR_OPTIONS.length];
    return getAvatarThumbSrc(item) || item?.url || null;
}

type DemoUserAccount = {
    id?: string | number;
    userId?: number;
    username?: string;
    player_name?: string;
    name?: string;
    avatar?: string;
};

function resolveViewerUserId(userAccount?: DemoUserAccount): number | string {
    const raw = userAccount?.id ?? userAccount?.userId ?? SQUARES_DEMO_VIEWER_USER_ID;
    if (raw == null || raw === "") return SQUARES_DEMO_VIEWER_USER_ID;
    const asNumber = Number(raw);
    return Number.isFinite(asNumber) ? asNumber : String(raw);
}

function resolveViewerDisplayName(userAccount?: DemoUserAccount): string {
    return userAccount?.player_name || userAccount?.username || userAccount?.name || "You";
}

function resolveViewerAvatar(userAccount?: DemoUserAccount): string | null {
    return userAccount?.avatar ?? squaresDemoViewerAvatar();
}

function withAvatarFields(avatar: string | null) {
    return { avatar, profile_pic: avatar };
}

const BALL_MS = 2400;
let simTimer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<Listener>();

function emit() {
    listeners.forEach((fn) => fn());
}

function chipsPerBall(totalChips: number): number {
    const per = Math.floor(totalChips / BALL_COUNT);
    return per > 0 ? per : 1;
}

function buildSelectionsFromPicks(
    picks: readonly string[],
    grid: ReturnType<typeof buildSquaresDemoOddsGrid>,
): DemoSelection {
    const perBall = chipsPerBall(100);
    const selections: DemoSelection = {};
    for (let ball = 1; ball <= 6; ball += 1) {
        const runLabel = picks[ball - 1] ?? "1";
        const odds = parseFloat(grid.cells[runLabel]?.[ball] ?? "3");
        selections[String(ball)] = {
            run: runLabel,
            runLabel,
            chips: perBall,
            odds: Number.isFinite(odds) ? odds : 3,
        };
    }
    return selections;
}

function runningChipsFor(selections: DemoSelection, ballResults: string[], superMode = false): number {
    let sum = 0;
    for (let ball = 1; ball <= ballResults.length; ball += 1) {
        const sel = selections[String(ball)];
        const result = ballResults[ball - 1];
        if (!sel || !result) continue;
        const pred = sel.run ?? sel.runLabel ?? "";
        const chips = Number(sel.chips) || 0;
        const odds = Number(sel.odds) || 0;
        if (chips <= 0 || odds <= 0 || !pred) continue;
        if (computeBallWon(result, pred)) sum += chips * odds;
    }
    const base = Math.round(sum);
    if (!superMode) return base;
    return computeSuperSquaresBreakdown(selections, ballResults).returnCoins;
}

function perBallFor(selections: DemoSelection, ballResults: string[]) {
    const n = Math.min(ballResults.length, 6);
    const out: Array<{ ball: number; result: string; selected: string | null; won: boolean }> = [];
    for (let i = 0; i < n; i += 1) {
        const ball = i + 1;
        const sel = selections[String(ball)];
        const pred = sel?.run ?? sel?.runLabel ?? "";
        const selected = pred ? String(pred).trim() : null;
        const result = ballResults[i] != null ? String(ballResults[i]).trim() : "";
        const computed = result && selected ? computeBallWon(result, selected) : null;
        const won = computed === true;
        out.push({ ball, result, selected, won });
    }
    return out;
}

function rankPlays(plays: DemoPlayRow[]): DemoPlayRow[] {
    const sorted = [...plays].sort((a, b) => {
        const ar = Number(a.runningChips ?? a.score ?? 0);
        const br = Number(b.runningChips ?? b.score ?? 0);
        if (br !== ar) return br - ar;
        return (a.id ?? 0) - (b.id ?? 0);
    });
    let lastScore: number | null = null;
    let rank = 0;
    return sorted.map((p, i) => {
        const score = Number(p.runningChips ?? p.score ?? 0);
        if (lastScore == null || score < lastScore) {
            rank = i + 1;
            lastScore = score;
        }
        return { ...p, rank };
    });
}

function enrichPlays(plays: DemoPlayRow[], ballsRevealed: number): DemoPlayRow[] {
    const results = activeBallResults();
    const raw = results.slice(0, ballsRevealed).map((b) => displayBallResult(b) ?? "0");
    const superMode = isSuperSquaresDemoContext();
    const enriched = plays.map((p) => {
        const runningChips = runningChipsFor(p.selections, raw, superMode);
        return {
            ...p,
            runningChips,
            per_ball: perBallFor(p.selections, raw),
            ball_results: ballsRevealed >= 6 ? [...results] : null,
            score: ballsRevealed >= 6 ? runningChips : p.score,
            settled: ballsRevealed >= 6,
            ...(superMode
                ? { super_squares: computeSuperSquaresBreakdown(p.selections, raw) }
                : {}),
        };
    });
    const ranked = rankPlays(enriched);
    if (ballsRevealed >= 6 && state.viewerSubmitted) {
        return ensureViewerWinsRanking(ranked);
    }
    return ranked;
}

function clearSimTimer() {
    if (simTimer != null) {
        clearInterval(simTimer);
        simTimer = null;
    }
}

const grid = buildSquaresDemoOddsGrid();

function demoOverNumber(): number {
    return isSuperSquaresDemoContext() ? PRESENTER_LIVE_OVER : SQUARES_DEMO_OVER_NUMBER;
}

function defaultBallResults(): string[] {
    return isSuperSquaresDemoContext() ? [...PRESENTER_LIVE_BALL_RESULTS] : [...SQUARES_DEMO_BALL_RESULTS];
}

function activeBallResults(): string[] {
    return state.scriptedBallResults;
}

/** Raw feed value that resolves as a win for the given Squares run label. */
function rawResultForRunLabel(runLabel: string): string {
    const run = String(runLabel).trim();
    if (run === "5+") return "6";
    if (run === "0") return "0";
    if (run === "1") return "1";
    if (run === "2") return "2";
    if (run === "3") return "3";
    if (run === "4") return "4";
    return "1";
}

/** Demo showcase: land each ball on the viewer's pick so their row goes green. */
function ballResultsFromViewerSelections(selections: DemoSelection): string[] {
    return Array.from({ length: 6 }, (_, i) => {
        const sel = selections[String(i + 1)];
        const run = sel?.run ?? sel?.runLabel;
        if (run == null || String(run).trim() === "") return "1";
        return rawResultForRunLabel(String(run));
    });
}

/**
 * League Super Squares demo: mostly hit the viewer's picks (accuracy tiers climb live)
 * and force a mid-over wicket so the ×2 celebration fires.
 */
function ballResultsForSuperSquaresDemo(selections: DemoSelection): string[] {
    const fromPicks = ballResultsFromViewerSelections(selections);
    const out = [...fromPicks];
    out[3] = "W";
    return out;
}

function ensureViewerWinsRanking(plays: DemoPlayRow[]): DemoPlayRow[] {
    const uid = String(state.viewerUserId);
    const top = Math.max(0, ...plays.map((p) => Number(p.runningChips ?? 0)));
    const boosted = plays.map((p) => {
        if (!p.isBot && String(p.user_id) === uid) {
            return { ...p, runningChips: Math.max(top + 1, Number(p.runningChips ?? 0)) };
        }
        return p;
    });
    return rankPlays(boosted);
}

const state = {
    phase: "idle" as DemoPhase,
    ballsRevealed: 0,
    refreshNonce: 0,
    viewerPlayId: 900001,
    plays: [] as DemoPlayRow[],
    viewerUserId: SQUARES_DEMO_VIEWER_USER_ID as number | string,
    viewerSubmitted: false,
    scriptedBallResults: defaultBallResults(),
    oddsLocked: { [SQUARES_DEMO_OVER_NUMBER]: true, [SQUARES_DEMO_OVER_NUMBER + 1]: true } as Record<
        number,
        boolean
    >,
};

function resolveSubmittedOverNumber(overNumber?: number): number {
    const n = Math.floor(Number(overNumber));
    return Number.isFinite(n) && n >= 1 ? n : demoOverNumber();
}

function buildViewerPlay(
    selections: DemoSelection,
    userAccount?: DemoUserAccount,
    overNumber?: number,
): DemoPlayRow {
    const userId = resolveViewerUserId(userAccount);
    const avatar = resolveViewerAvatar(userAccount);
    return {
        id: state.viewerPlayId,
        user_id: userId,
        name: resolveViewerDisplayName(userAccount),
        ...withAvatarFields(avatar),
        isBot: false,
        selections,
        created_at: new Date().toISOString(),
        stake: SQUARES_ENTRANCE_FEE,
        ball_results: null,
        rank: null,
        score: null,
        winnings: null,
        settled: false,
        outcome: null,
        return: null,
        totalPot: null,
        over_number: resolveSubmittedOverNumber(overNumber),
    };
}

function seedBotPlays(): DemoPlayRow[] {
    return DEMO_OPPONENTS.map((opp, idx) => {
        const avatar = demoBotAvatarUrl(opp.avatarIndex);
        return {
            id: 900010 + idx,
            user_id: opp.userId,
            name: opp.name,
            ...withAvatarFields(avatar),
            isBot: true,
            selections: buildSelectionsFromPicks(opp.picks, grid),
            created_at: new Date().toISOString(),
            stake: SQUARES_ENTRANCE_FEE,
            ball_results: null,
            rank: null,
            score: null,
            winnings: null,
            settled: false,
            outcome: null,
            return: null,
            totalPot: null,
            over_number: demoOverNumber(),
        };
    });
}

function upsertSubmittedViewerProfile(userAccount?: DemoUserAccount) {
    if (!state.viewerSubmitted) return;
    const userId = resolveViewerUserId(userAccount);
    state.viewerUserId = userId;
    const humanIdx = state.plays.findIndex((p) => !p.isBot);
    if (humanIdx < 0) return;
    const viewerPlay = {
        ...state.plays[humanIdx],
        user_id: userId,
        name: resolveViewerDisplayName(userAccount),
        ...withAvatarFields(resolveViewerAvatar(userAccount)),
    };
    state.plays = [viewerPlay, ...state.plays.filter((p) => p.isBot)];
}

function resetInternal() {
    clearSimTimer();
    state.phase = "idle";
    state.ballsRevealed = 0;
    state.refreshNonce = 0;
    state.viewerPlayId = 900001;
    state.viewerUserId = SQUARES_DEMO_VIEWER_USER_ID;
    state.viewerSubmitted = false;
    state.scriptedBallResults = defaultBallResults();
    state.plays = seedBotPlays();
}

resetInternal();

export function subscribeSquaresDemoState(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function resetSquaresDemoState() {
    resetInternal();
    emit();
}

/** Sync viewer name/avatar after submit when the account payload resolves (e.g. wallet load). */
export function configureSquaresDemoViewer(userAccount?: DemoUserAccount) {
    if (!state.viewerSubmitted) return;
    upsertSubmittedViewerProfile(userAccount);
    emit();
}

export function getSquaresDemoSnapshot() {
    return {
        phase: state.phase,
        ballsRevealed: state.ballsRevealed,
        refreshNonce: state.refreshNonce,
        ballResults: activeBallResults(),
        plays: enrichPlays(state.plays, state.ballsRevealed),
        oddsLocked: state.oddsLocked,
    };
}

function bumpRefresh() {
    state.refreshNonce += 1;
}

/** When the scripted over finishes in a prototype league, settle the open Super Squares play. */
function settleProtoLeagueSquaresFromDemo(viewerPlay: DemoPlayRow) {
    const leagueId = resolveDemoLeagueId();
    if (!leagueId) return;
    const league = findLeague(leagueId);
    if (!league) return;
    const uid = Number(viewerPlay.user_id);
    if (!Number.isFinite(uid)) return;
    const overNum = Number(viewerPlay.over_number) || demoOverNumber();
    const open = league.plays.find(
        (row) =>
            row.status === "open" &&
            row.game === "squares" &&
            row.userId === uid &&
            Number(row.marketDetails?.overNumber) === overNum,
    );
    if (!open) return;
    const balls = Array.isArray(viewerPlay.ball_results)
        ? viewerPlay.ball_results.map((b) => String(b ?? ""))
        : activeBallResults();
    const superBr =
        viewerPlay.super_squares ??
        (open.marketDetails?.selections
            ? computeSuperSquaresBreakdown(open.marketDetails.selections, balls)
            : null);
    const returned = Math.max(
        0,
        Math.round(Number(superBr?.returnCoins ?? viewerPlay.runningChips ?? viewerPlay.score ?? 0) || 0),
    );
    settleOpenPlayWithReturn(leagueId, open.id, returned, {
        ...(balls.length > 0 ? { ballResults: balls } : {}),
        ...(superBr
            ? {
                  superSquares: {
                      baseReturn: superBr.baseReturn,
                      wicketCount: superBr.wicketCount,
                      correctCount: superBr.correctCount,
                      streakLength: superBr.streakLength,
                      streakBonus: superBr.streakBonus,
                      wicketMult: superBr.wicketMult,
                      accuracyMult: superBr.accuracyMult,
                      combinedMult: superBr.combinedMult,
                      returnCoins: superBr.returnCoins,
                  },
              }
            : {}),
    });
}

function advanceBallInternal(): boolean {
    if (state.ballsRevealed >= 6) return true;
    state.ballsRevealed += 1;
    if (state.ballsRevealed >= 6) {
        state.phase = "settled";
        const settled = enrichPlays(state.plays, 6).map((p) => ({
            ...p,
            settled: true,
            score: p.runningChips ?? 0,
            winnings: p.rank === 1 ? squaresDemoFirstPlacePrize() : null,
            outcome: p.rank === 1 ? "WIN" : "LOSE",
            return: p.rank === 1 ? squaresDemoFirstPlacePrize() : 0,
        }));
        state.plays = settled;
        const viewer = settled.find((p) => !p.isBot && String(p.user_id) === String(state.viewerUserId));
        if (viewer) settleProtoLeagueSquaresFromDemo(viewer);
        clearSimTimer();
        bumpRefresh();
        emit();
        return true;
    }
    state.plays = enrichPlays(state.plays, state.ballsRevealed);
    bumpRefresh();
    emit();
    return false;
}

function startSimTimer() {
    clearSimTimer();
    const tick = () => {
        if (advanceBallInternal()) clearSimTimer();
    };
    tick();
    simTimer = setInterval(tick, BALL_MS);
}

export function recordSquaresDemoSubmit(
    selections: DemoSelection,
    userAccount?: DemoUserAccount,
    overNumber?: number,
) {
    state.viewerUserId = resolveViewerUserId(userAccount);
    const viewerPlay = buildViewerPlay(selections, userAccount, overNumber);
    state.plays = [viewerPlay, ...state.plays.filter((p) => p.isBot || p.over_number !== viewerPlay.over_number)];
    state.viewerSubmitted = true;
    const liveOver = demoOverNumber();
    const shouldSimulateLiveOver = viewerPlay.over_number === liveOver;
    if (shouldSimulateLiveOver) {
        state.phase = "simulating";
        state.ballsRevealed = 0;
        state.scriptedBallResults = isSuperSquaresDemoContext()
            ? ballResultsForSuperSquaresDemo(selections)
            : ballResultsFromViewerSelections(selections);
        bumpRefresh();
        emit();
        startSimTimer();
    } else {
        bumpRefresh();
        emit();
    }
    return { success: true, playId: viewerPlay.id };
}

export function buildSquaresDemoMatchOverPlaysResponse() {
    const plays = enrichPlays(state.plays, state.ballsRevealed);
    const pool = squaresDemoPoolEntrantCount(state.plays.length);
    // League Super Squares is points-model — no shared 1st-place coin prize.
    const top = isSuperSquaresDemoContext() ? 0 : squaresDemoFirstPlacePrize();
    const results = activeBallResults();
    const ballResults =
        state.ballsRevealed >= 6 ? [...results] : null;
    const overs: Record<number, { winners: unknown[]; ball_results: string[] | null; total_pot: number }> = {};
    if (ballResults) {
        overs[demoOverNumber()] = {
            winners: plays,
            ball_results: ballResults,
            total_pot: pool * SQUARES_ENTRANCE_FEE,
        };
    }
    return {
        plays,
        overs,
        overOddsLocked: state.oddsLocked,
        over_economy: {
            [demoOverNumber()]: {
                pool_entrant_count: pool,
                top_prize: top,
                min_players: 4,
            },
            [demoOverNumber() + 1]: {
                pool_entrant_count: pool,
                top_prize: top,
                min_players: 4,
            },
        },
    };
}

export function buildSquaresDemoOverPlaysResponse(overNumber: number, ballResultsParam?: string[] | null) {
    const overNum = Number(overNumber);
    const plays = enrichPlays(
        state.plays.filter((p) => p.over_number === overNum),
        state.ballsRevealed,
    );
    let ballResults = ballResultsParam;
    if (ballResults == null && state.ballsRevealed > 0) {
        ballResults = activeBallResults()
            .slice(0, state.ballsRevealed)
            .map((b) => displayBallResult(b) ?? "0");
    }
    const pool = squaresDemoPoolEntrantCount(state.plays.length);
    const ballsForEnrichment =
        ballResults && ballResults.length > 0
            ? ballResults.length
            : state.ballsRevealed;
    return {
        plays: ballsForEnrichment > 0 ? enrichPlays(plays, ballsForEnrichment) : plays,
        ball_results: ballResults,
        min_players: 4,
        pool_entrant_count: pool,
        top_prize: isSuperSquaresDemoContext() ? 0 : squaresDemoFirstPlacePrize(),
    };
}

export function buildSquaresDemoMyOversInMatch(viewerUserId: number | string | null | undefined) {
    const uid = viewerUserId != null ? String(viewerUserId) : String(state.viewerUserId);
    const overNumbers = [
        ...new Set(
            state.plays
                .filter((p) => !p.isBot && (uid === "" || String(p.user_id) === uid))
                .map((p) => Number(p.over_number))
                .filter((n) => Number.isFinite(n) && n >= 1),
        ),
    ].sort((a, b) => a - b);
    return { overNumbers };
}
